import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, useClerk, SignIn } from "@clerk/clerk-react";
import { useCollectionsStore } from "../store/collectionsStore";
import { api } from "../services/api";
import type {
  AcceptCollectionInviteResult,
  CollectionInviteLinkInfo,
} from "@poi-planner/shared";
import clsx from "clsx";
import {
  getInviteError,
  type InviteState,
  useInviteAcceptance,
} from "../hooks/useInviteAcceptance";

const ACCEPT_ERROR_MESSAGES = {
  OWN_COLLECTION: "You already own this collection",
  INVALID_LINK: "This invite link is invalid or has expired",
};

export function CollectionInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const { loadCollections } = useCollectionsStore();

  const [inviteInfo, setInviteInfo] = useState<CollectionInviteLinkInfo | null>(
    null,
  );
  const [state, setState] = useState<InviteState>("loading");
  const [error, setError] = useState<string | null>(null);

  // Load invite info on mount
  useEffect(() => {
    if (!token) {
      setState("invalid");
      setError("No invite token provided");
      return;
    }

    const loadInviteInfo = async () => {
      try {
        const info = await api.getCollectionInviteLinkInfo(token);
        setInviteInfo(info);

        if (info.isValid) {
          setState("valid");
        } else {
          setState("invalid");
          setError("This invite link is invalid or has expired");
        }
      } catch (err: unknown) {
        const inviteError = getInviteError(err);
        setState("error");
        setError(inviteError.message || "Failed to load invite information");
      }
    };

    loadInviteInfo();
  }, [token]);

  const onAccepted = useCallback(
    async (_result: AcceptCollectionInviteResult) => {
      // Reload collections and redirect
      await loadCollections();
      setTimeout(() => {
        navigate("/collections");
      }, 1500);
    },
    [loadCollections, navigate],
  );

  const handleAccept = useInviteAcceptance({
    token,
    isLoaded,
    isSignedIn,
    state,
    setState,
    setError,
    accept: api.acceptCollectionInviteLink,
    onAccepted,
    errorMessages: ACCEPT_ERROR_MESSAGES,
    fallbackMessage: "Failed to accept invite",
  });

  // Wait for Clerk to load
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-gray-200 border-t-amber-500 rounded-full animate-spin"></div>
          <span className="text-sm text-secondary font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  // Loading state
  if (state === "loading") {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-gray-200 border-t-amber-500 rounded-full animate-spin"></div>
          <span className="text-sm text-secondary font-medium">
            Loading invite...
          </span>
        </div>
      </div>
    );
  }

  // Invalid/expired state
  if (state === "invalid" || (state === "error" && !inviteInfo?.isValid)) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-error-bg rounded-full blur-3xl" />
        </div>

        <div className="w-full max-w-md relative">
          <div className="bg-white rounded-2xl border border-border shadow-xl shadow-black/5 p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-bg-strong mb-5">
              <span className="material-symbols-outlined text-3xl text-error">
                link_off
              </span>
            </div>

            <h1 className="text-2xl font-bold text-primary tracking-tight">
              Invalid Invite Link
            </h1>
            <p className="text-sm text-secondary mt-2 mb-6">
              {error ||
                "This invite link is invalid, has expired, or has already been used."}
            </p>

            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-primary text-white border-none rounded-xl text-sm font-bold cursor-pointer font-sans transition-all hover:opacity-90"
            >
              Go to PlotPlanGo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Accepted state
  if (state === "accepted") {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-success-bg rounded-full blur-3xl" />
        </div>

        <div className="w-full max-w-md relative">
          <div className="bg-white rounded-2xl border border-border shadow-xl shadow-black/5 p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-bg-strong mb-5">
              <span className="material-symbols-outlined text-3xl text-success">
                check_circle
              </span>
            </div>

            <h1 className="text-2xl font-bold text-primary tracking-tight">
              You're In!
            </h1>
            <p className="text-sm text-secondary mt-2 mb-4">
              You now have access to{" "}
              <strong>{inviteInfo?.collectionName}</strong>
            </p>
            <p className="text-xs text-tertiary">
              Redirecting to collections...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main invite page (valid invite)
  return (
    <div className="min-h-screen bg-page flex items-center justify-center p-6">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <div className="bg-white rounded-2xl border border-border shadow-xl shadow-black/5 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center border-b border-border/50 bg-gradient-to-b from-amber-50/50 to-transparent">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-amber-500 mb-4 shadow-lg">
              <span className="material-symbols-outlined text-2xl text-white">
                folder_special
              </span>
            </div>
            <p className="text-sm text-secondary mb-2">
              You've been invited to join
            </p>
            <h1 className="text-2xl font-bold text-primary tracking-tight">
              {inviteInfo?.collectionName || "Collection"}
            </h1>
          </div>

          {/* Invite Details */}
          <div className="px-8 py-6 bg-gradient-to-b from-slate-50/50 to-transparent border-b border-border/50">
            <div className="flex items-center justify-center gap-6">
              <div className="text-center">
                <span
                  className={clsx(
                    "inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide",
                    inviteInfo?.permissionLevel === "editor"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-purple-100 text-purple-700",
                  )}
                >
                  <span className="material-symbols-outlined text-sm mr-1">
                    {inviteInfo?.permissionLevel === "editor"
                      ? "edit"
                      : "visibility"}
                  </span>
                  {inviteInfo?.permissionLevel} access
                </span>
              </div>
              {inviteInfo?.ownerEmail && (
                <div className="text-center">
                  <p className="text-xs text-tertiary">Invited by</p>
                  <p className="text-sm font-medium text-secondary">
                    {inviteInfo.ownerEmail}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {error && (
              <div className="flex items-start gap-3 p-4 mb-5 bg-error-bg border border-error/15 rounded-xl text-sm text-error">
                <span className="material-symbols-outlined text-error text-lg flex-shrink-0 mt-0.5">
                  error
                </span>
                <p>{error}</p>
              </div>
            )}

            {isSignedIn ? (
              <div className="text-center">
                <p className="text-sm text-secondary mb-4">
                  Signed in as{" "}
                  <strong>
                    {clerk.user?.primaryEmailAddress?.emailAddress}
                  </strong>
                </p>
                <button
                  onClick={handleAccept}
                  disabled={state === "accepting"}
                  className={clsx(
                    "w-full py-3.5 text-sm font-bold rounded-xl border-none cursor-pointer font-sans transition-all",
                    "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600",
                    "shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30",
                    "active:scale-[0.98]",
                    state === "accepting" && "opacity-70 cursor-not-allowed",
                  )}
                >
                  {state === "accepting" ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Joining collection...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-lg">
                        group_add
                      </span>
                      Accept Invite
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <div>
                <p className="text-center text-sm text-secondary mb-5">
                  Sign in to accept this invitation
                </p>
                <SignIn
                  appearance={{
                    elements: {
                      rootBox: "w-full",
                      card: "shadow-none border-0 p-0",
                    },
                  }}
                  redirectUrl={window.location.href}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
